import { apiClient } from '../client';
import { DateRangeKey } from '../../config/googleAnalytics';

/**
 * Google Analytics Data API Service
 *
 * This service provides methods to fetch data from Google Analytics 4 (GA4)
 * using the Google Analytics Data API.
 */

// User Metrics
export interface GAUserMetrics {
  totalUsers: number;
  newUsers: number;
  activeUsers: number;
  returningUsers: number;
  usersByCountry: Array<{
    country: string;
    users: number;
  }>;
  usersByDevice: Array<{
    deviceCategory: string;
    users: number;
  }>;
}

// Session Metrics
export interface GASessionMetrics {
  sessions: number;
  averageSessionDuration: number;
  bounceRate: number;
  sessionsPerUser: number;
  sessionsByHour: Array<{
    hour: number;
    sessions: number;
  }>;
}

// Engagement Metrics
export interface GAEngagementMetrics {
  engagementRate: number;
  engagedSessions: number;
  averageEngagementTime: number;
  eventCount: number;
  topEvents: Array<{
    eventName: string;
    eventCount: number;
  }>;
}

// Page Metrics
export interface GAPageMetrics {
  pageViews: number;
  uniquePageViews: number;
  averageTimeOnPage: number;
  topPages: Array<{
    pagePath: string;
    pageTitle: string;
    views: number;
    avgTimeOnPage: number;
  }>;
}

// Acquisition Metrics
export interface GAAcquisitionMetrics {
  usersBySource: Array<{
    source: string;
    medium: string;
    users: number;
    sessions: number;
  }>;
  usersByCampaign: Array<{
    campaign: string;
    users: number;
    conversions: number;
  }>;
}

// Conversion Metrics
export interface GAConversionMetrics {
  conversions: number;
  conversionRate: number;
  conversionsByEvent: Array<{
    eventName: string;
    conversions: number;
    conversionRate: number;
  }>;
}

// Real-time Metrics
export interface GARealTimeMetrics {
  activeUsers: number;
  screenPageViewsPerMinute: number;
  topPages: Array<{
    pagePath: string;
    activeUsers: number;
  }>;
  usersByCountry: Array<{
    country: string;
    activeUsers: number;
  }>;
}

// Query parameters
export interface GAQueryParams {
  dateRange?: DateRangeKey;
  startDate?: string;
  endDate?: string;
  dimensions?: string[];
  metrics?: string[];
  limit?: number;
}

export const googleAnalyticsAPI = {
  // User Metrics
  getUserMetrics: async (params: GAQueryParams = {}): Promise<GAUserMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/users', { params });
    return response.data;
  },

  // Session Metrics
  getSessionMetrics: async (params: GAQueryParams = {}): Promise<GASessionMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/sessions', { params });
    return response.data;
  },

  // Engagement Metrics
  getEngagementMetrics: async (params: GAQueryParams = {}): Promise<GAEngagementMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/engagement', { params });
    return response.data;
  },

  // Page Metrics
  getPageMetrics: async (params: GAQueryParams = {}): Promise<GAPageMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/pages', { params });
    return response.data;
  },

  // Acquisition Metrics
  getAcquisitionMetrics: async (params: GAQueryParams = {}): Promise<GAAcquisitionMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/acquisition', { params });
    return response.data;
  },

  // Conversion Metrics
  getConversionMetrics: async (params: GAQueryParams = {}): Promise<GAConversionMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/conversions', { params });
    return response.data;
  },

  // Real-time Metrics
  getRealTimeMetrics: async (): Promise<GARealTimeMetrics> => {
    const response = await apiClient.get('/api/admin/google-analytics/realtime');
    return response.data;
  },

  // Custom Report
  getCustomReport: async (params: GAQueryParams): Promise<unknown> => {
    const response = await apiClient.post('/api/admin/google-analytics/custom-report', params);
    return response.data;
  },
};
